import React from "react";
import {pdf} from "@react-pdf/renderer";
import {apiInstance} from "../../../../../../api/api";
import Section from "../../../../../../models/Section";
import {CostCenterDrawing} from "../../../../../../models/CostCenterDrawing";
import {CostCenter} from "../../../../../../models/CostCenter";
import {SymbolCacheDataContextBridge, useSymbolCacheDataContext} from "../../../../../../providers/SymbolCacheDataProvider";
import {AssetPlanSummaryPDF, AssetTableData} from "./AssetPlanSummaryPDF";
import {CountMeasurement} from "../../../../../project-drawings/features/editor/models/editor";
import {DrawingFileInfo, getSelectedDrawingFileInfo} from "./util";
import AssetCustomField from "../../../../../../models/AssetCustomField";

export function useAssetPlanSummaryPDF() {
	const symbolCache = useSymbolCacheDataContext()

	async function createAssetSummaryPDF(drawingFileInfo: DrawingFileInfo,
									assetTableData: AssetTableData[], fileName: string = "summary.pdf") {
		const summary = await pdf(
			<SymbolCacheDataContextBridge.Provider value={symbolCache}>
				<AssetPlanSummaryPDF
					drawingFileInfo={drawingFileInfo}
					assetTables={assetTableData}
				/>
			</SymbolCacheDataContextBridge.Provider>
		).toBlob();
		return new File([summary], fileName);
	}

    async function createAssetTableData(countGroups: CountMeasurement[], siteId: number): Promise<AssetTableData[]> {
        return await Promise.all(
            countGroups.map(async (group) => {
                if (!group.material.assetTypeId) {
                    return { headers: [], rows: [], materialName:'' };
                }

                const materialName = group.material.name;
                const headers = await apiInstance.assetCustomFieldsApi.fetchAssetCustomFields(group.material.assetTypeId, siteId);

                const assetID = new AssetCustomField(-1, "Asset ID", "string", [])
                headers.unshift(assetID)

                const rowPromises: Promise<{ [key: number]: string } | null>[] = [];

                for (const item of (group.countItems || [])) {
                    if (item.assetId) {
                        rowPromises.push((async () => {
                            const values = await apiInstance.assetCustomFieldsApi.fetchCustomFieldsByCountItem(item.id, item.assetId!);

                            const rowData: { [key: number]: string } = {};
                            headers.forEach(header => {
                                if (header.id === -1) {
                                    const foundValue = values[0]?.assetId;
                                    rowData[header.id] = foundValue ? String(foundValue) : '';
                                } else {
                                    const foundValue = values[0]?.customFields?.find(v => v.customFieldName === header.name);
                                    rowData[header.id] = foundValue ? String(foundValue.value) : '';
                                }
                            });
                            return rowData;
                        })());
                    }
                }

                const rows = await Promise.all(rowPromises);

                return {
                    headers,
                    rows: rows.filter(row => row !== null) as { [key: number]: string }[],
                    materialName,
                };
            })
        );
    }

	async function createAssetPlanSummaryAttachment(
		file: File,
		section: Section,
		costCenter: CostCenter,
		drawing: CostCenterDrawing,
		countGroups: CountMeasurement[],
        siteId: number,
	): Promise<File> {
		const assetTables: AssetTableData[] = await createAssetTableData(countGroups, siteId)
		const drawingFileInfo = await getSelectedDrawingFileInfo(file, section, costCenter, drawing)
		return await createAssetSummaryPDF(drawingFileInfo, assetTables)
	}

    async function sendAssetPlanSummaryAttachmentToSite(
        file: File,
        section: Section,
        costCenter: CostCenter,
        drawing: CostCenterDrawing,
        countGroups: CountMeasurement[],
        siteId: number,
        fileName: string,
    ): Promise<Response> {
        const assetTables: AssetTableData[] = await createAssetTableData(countGroups, siteId)
        const drawingFileInfo = await getSelectedDrawingFileInfo(file, section, costCenter, drawing)
        const summaryFile = await createAssetSummaryPDF(drawingFileInfo, assetTables, fileName)
        return await apiInstance.assetsApi.postAssetSummaryAttachment(siteId, summaryFile)
    }

	return {
		createAssetPlanSummaryAttachment,
        sendAssetPlanSummaryAttachmentToSite
	};
}
